import React, { useState, useEffect, useContext } from 'react';
import { ApiContext } from '../../App';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  LineChart,
  Line,
  Legend
} from 'recharts';
import { Users, DollarSign, TrendingUp, Phone } from 'lucide-react';

const ProspectionStats = () => {
  const [stats, setStats] = useState(null);
  const [processStats, setProcessStats] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [period, setPeriod] = useState('all');
  const [selectedUserId, setSelectedUserId] = useState('all');
  const [companyUsers, setCompanyUsers] = useState([]);
  const API = useContext(ApiContext);

  // Récupérer la liste des utilisateurs de l'entreprise
  useEffect(() => {
    const fetchCompanyUsers = async () => {
      try {
        const response = await API.get('/api/user/company');
        setCompanyUsers(response.data || []);
      } catch (error) {
        console.error('Erreur lors du chargement des utilisateurs:', error);
      }
    };
    fetchCompanyUsers();
  }, [API]);

  const fetchStats = async () => {
    try {
      setLoading(true);
      setError('');

      const userParam = selectedUserId !== 'all' ? `&userId=${selectedUserId}` : '';

      const [globalResponse, processesResponse] = await Promise.all([
        API.get(`/api/prospection-stats?period=${period}${userParam}`),
        API.get(`/api/prospection-stats/by-process?period=${period}${userParam}`)
      ]);

      setStats(globalResponse.data);
      setProcessStats(processesResponse.data.data || []);
    } catch (error) {
      console.error('Erreur lors du chargement des stats de prospection:', error);
      setError('Erreur lors du chargement des données');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchStats();
  }, [period, selectedUserId]);

  const getPeriodLabel = () => {
    switch (period) {
      case 'month':
        return 'Ce mois-ci';
      case 'quarter':
        return 'Ce trimestre';
      case 'year':
        return 'Cette année';
      default:
        return 'Toutes périodes';
    }
  };

  // Préparer les données pour le graphique en barres
  const chartData = stats
    ? [
        //{ name: 'Lead', value: stats.stats.lead },
        { name: 'Contacté', value: stats.stats.contacte },
        { name: 'À relancer', value: stats.stats.aRelancer },
        { name: 'Relancé 1', value: stats.stats.relance1 },
        { name: 'Relancé 2', value: stats.stats.relance2 },
        { name: 'Relancé 3', value: stats.stats.relance3 },
        { name: 'A répondu', value: stats.stats.aRepondu },
        { name: 'RDV planifié', value: stats.stats.rdvPlanifie },
        { name: 'RDV réalisé', value: stats.stats.rdvRealise },
        { name: 'Offre', value: stats.stats.offre },
        { name: 'Négociation', value: stats.stats.negociation },
        { name: 'Deal', value: stats.stats.deal },
        { name: 'Refus', value: stats.stats.refus }
      ].filter(item => item.value > 0)
    : [];

  // Préparer les données pour le funnel de conversion
  const conversionData = stats
    ? [
        //{ stage: 'Lead', count: stats.stats.lead },
        { stage: 'Contactés', count: stats.stats.contacte },
        { stage: 'A répondu', count: stats.stats.aRepondu },
        { stage: 'RDV réalisé', count: stats.stats.rdvRealise },
        { stage: 'Offre', count: stats.stats.offre },
        { stage: 'Deal', count: stats.stats.deal }
      ]
    : [];

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('fr-FR', {
      style: 'currency',
      currency: 'EUR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(value || 0);
  };

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-white p-3 border rounded shadow-lg">
          <p className="font-medium">{label}</p>
          <p className="text-blue-600">{`Prospects: ${payload[0].value}`}</p>
        </div>
      );
    }
    return null;
  };

  if (loading) {
    return (
      <div className="p-4 sm:p-6 lg:p-10 min-h-screen pt-4 sm:pt-8 lg:pt-16">
        <div className="flex flex-col items-center justify-center h-64 space-y-4">
          <div className="w-12 h-12 sm:w-16 sm:h-16 border-4 border-slate-200 border-t-indigo-500 rounded-full animate-spin"></div>
          <span className="text-base sm:text-lg">Chargement des statistiques...</span>
        </div>
      </div>
    );
  }

  if (!stats) return null;

  return (
    <div className="min-h-screen px-2 sm:px-0">
      <header className="mb-4 sm:mb-6 lg:mb-8 flex flex-col sm:flex-row justify-between items-start sm:items-center space-y-4 sm:space-y-0">
        <div>
          <p className="text-sm sm:text-base text-slate-600">
            Visualisez vos performances de prospection.
          </p>
        </div>

        {/* Sélecteurs de filtre */}
        <div className="flex flex-col sm:flex-row items-start sm:items-center space-y-2 sm:space-y-0 sm:space-x-4">
          {/* Sélecteur d'utilisateur */}
          <div className="flex items-center space-x-2">
            <label className="text-sm font-medium text-slate-700">Utilisateur:</label>
            <select
              value={selectedUserId}
              onChange={(e) => setSelectedUserId(e.target.value)}
              className="border border-slate-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
            >
              <option value="all">Tous</option>
              {companyUsers.map((user) => (
                <option key={user._id} value={user._id}>
                  {user.name}
                </option>
              ))}
            </select>
          </div>

          {/* Sélecteur de période */}
          <div className="flex items-center space-x-2">
            <label className="text-sm font-medium text-slate-700">Période:</label>
            <select
              value={period}
              onChange={(e) => setPeriod(e.target.value)}
              className="border border-slate-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
            >
              <option value="all">Toutes périodes</option>
              <option value="month">Ce mois-ci</option>
              <option value="quarter">Ce trimestre</option>
              <option value="year">Cette année</option>
            </select>
          </div>
        </div>
      </header>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded mb-6">
          {error}
        </div>
      )}

      {/* Cartes de résumé */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6 mb-6 sm:mb-8">
        <div className="bg-white p-4 sm:p-6 rounded-lg border">
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-sm sm:text-lg font-medium text-slate-600">Total Prospects</h3>
            <Users className="text-blue-500" size={24} />
          </div>
          <p className="text-2xl sm:text-3xl font-bold text-blue-600">
            {stats.stats.totalProspects}
          </p>
          <p className="text-xs text-slate-500 mt-1">{getPeriodLabel()}</p>
        </div>

        <div className="bg-white p-4 sm:p-6 rounded-lg border">
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-sm sm:text-lg font-medium text-slate-600">RDV réalisés</h3>
            <Phone className="text-purple-500" size={24} />
          </div>
          <p className="text-2xl sm:text-3xl font-bold text-purple-600">
            {stats.stats.rdvRealise}
          </p>
          <p className="text-xs text-slate-500 mt-1">
            {stats.stats.offre} offres envoyées
          </p>
        </div>

        <div className="bg-white p-4 sm:p-6 rounded-lg border">
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-sm sm:text-lg font-medium text-slate-600">Deals signés</h3>
            <TrendingUp className="text-green-500" size={24} />
          </div>
          <p className="text-2xl sm:text-3xl font-bold text-green-600">
            {stats.deals.totalDeals}
          </p>
          <p className="text-xs text-slate-500 mt-1">
            {stats.stats.deal} prospects deal
          </p>
        </div>

        <div className="bg-white p-4 sm:p-6 rounded-lg border">
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-sm sm:text-lg font-medium text-slate-600">CA généré</h3>
            <DollarSign className="text-orange-500" size={24} />
          </div>
          <p className="text-2xl sm:text-3xl font-bold text-orange-600">
            {formatCurrency(stats.deals.totalRevenue)}
          </p>
          <p className="text-xs text-slate-500 mt-1">
            {formatCurrency(stats.deals.totalBonus)} en primes
          </p>
        </div>
      </div>

      {/* Graphiques */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6 lg:gap-8 mb-8">
        {/* Répartition par étape */}
        <div className="bg-white p-4 sm:p-6 rounded-lg border ">
          <h2 className="text-lg sm:text-xl font-semibold text-slate-800 mb-4 sm:mb-6">
            Répartition par étape
          </h2>

          {chartData.length > 0 ? (
            <div className="h-72 sm:h-96">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart
                  data={chartData}
                  margin={{ top: 20, right: 10, left: 10, bottom: 80 }}
                >
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis
                    dataKey="name"
                    angle={-45}
                    textAnchor="end"
                    height={80}
                    fontSize={10}
                  />
                  <YAxis fontSize={10} />
                  <Tooltip content={<CustomTooltip />} />
                  <Bar dataKey="value" fill="#a855f7" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          ) : (
            <div className="flex items-center justify-center h-64 text-slate-500">
              Aucune donnée disponible
            </div>
          )}
        </div>

        {/* Funnel de conversion */}
        <div className="bg-white p-4 sm:p-6 rounded-lg border ">
          <h2 className="text-lg sm:text-xl font-semibold text-slate-800 mb-4 sm:mb-6">
            Funnel de conversion
          </h2>

          {conversionData.length > 0 ? (
            <div className="h-72 sm:h-96">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart
                  data={conversionData}
                  margin={{ top: 20, right: 30, left: 20, bottom: 20 }}
                >
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="stage" fontSize={10} angle={-15} textAnchor="end" height={60} />
                  <YAxis fontSize={10} />
                  <Tooltip />
                  <Legend />
                  <Line
                    type="monotone"
                    dataKey="count"
                    stroke="#a855f7"
                    strokeWidth={2}
                    dot={{ r: 5 }}
                    name="Prospects"
                  />
                </LineChart>
              </ResponsiveContainer>
            </div>
          ) : (
            <div className="flex items-center justify-center h-64 text-slate-500">
              Aucune donnée disponible
            </div>
          )}
        </div>
      </div>

      {/* Taux de conversion */}
      <div className="bg-white p-4 sm:p-6 rounded-lg border  mb-8">
        <h2 className="text-lg sm:text-xl font-semibold text-slate-800 mb-4 sm:mb-6">
          Taux de conversion
        </h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
          <div className="p-4 bg-slate-50 rounded-lg">
            <p className="text-xs sm:text-sm text-slate-600 mb-1">Contacté → A répondu</p>
            <p className="text-xl sm:text-2xl font-bold text-indigo-600">
              {stats.conversionRates.contacteToRepondu}%
            </p>
          </div>
          <div className="p-4 bg-slate-50 rounded-lg">
            <p className="text-xs sm:text-sm text-slate-600 mb-1">A répondu → RDV</p>
            <p className="text-xl sm:text-2xl font-bold text-purple-600">
              {stats.conversionRates.reponduToRdv}%
            </p>
          </div>
          <div className="p-4 bg-slate-50 rounded-lg">
            <p className="text-xs sm:text-sm text-slate-600 mb-1">RDV → Offre</p>
            <p className="text-xl sm:text-2xl font-bold text-orange-600">
              {stats.conversionRates.rdvToOffre}%
            </p>
          </div>
          <div className="p-4 bg-slate-50 rounded-lg">
            <p className="text-xs sm:text-sm text-slate-600 mb-1">Offre → Deal</p>
            <p className="text-xl sm:text-2xl font-bold text-green-600">
              {stats.conversionRates.offreToDeal}%
            </p>
          </div>
          <div className="p-4 bg-slate-50 rounded-lg">
            <p className="text-xs sm:text-sm text-slate-600 mb-1">Conversion globale</p>
            <p className="text-xl sm:text-2xl font-bold text-blue-600">
              {stats.conversionRates.globalConversion}%
            </p>
          </div>
        </div>
      </div>

      {/* Statistiques financières des deals */}
      {stats.deals.totalDeals > 0 && (
        <div className="bg-white p-4 sm:p-6 rounded-lg border  mb-8">
          <h2 className="text-lg sm:text-xl font-semibold text-slate-800 mb-4 sm:mb-6">
            Statistiques financières
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div className="p-4 bg-green-50 rounded-lg border border-green-200">
              <p className="text-sm text-slate-600 mb-2">Chiffre d'affaires total</p>
              <p className="text-2xl font-bold text-green-600">
                {formatCurrency(stats.deals.totalRevenue)}
              </p>
            </div>
            <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
              <p className="text-sm text-slate-600 mb-2">Primes totales</p>
              <p className="text-2xl font-bold text-blue-600">
                {formatCurrency(stats.deals.totalBonus)}
              </p>
            </div>
            <div className="p-4 bg-purple-50 rounded-lg border border-purple-200">
              <p className="text-sm text-slate-600 mb-2">Salaire moyen</p>
              <p className="text-2xl font-bold text-purple-600">
                {formatCurrency(stats.deals.avgSalary)}
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Statistiques par processus */}
      {processStats.length > 0 && (
        <div className="bg-white p-4 sm:p-6 rounded-lg border ">
          <h2 className="text-lg sm:text-xl font-semibold text-slate-800 mb-4 sm:mb-6">
            Performance par processus
          </h2>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-slate-200">
              <thead className="bg-slate-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase sticky left-0 bg-slate-50">
                    Processus
                  </th>
                  <th className="px-4 py-3 text-center text-xs font-medium text-slate-500 uppercase">
                    Total
                  </th>
                  {/* <th className="px-4 py-3 text-center text-xs font-medium text-slate-500 uppercase">
                    Lead
                  </th> */}
                  <th className="px-4 py-3 text-center text-xs font-medium text-slate-500 uppercase">
                    Contacté
                  </th>
                  <th className="px-4 py-3 text-center text-xs font-medium text-slate-500 uppercase">
                    A répondu
                  </th>
                  <th className="px-4 py-3 text-center text-xs font-medium text-slate-500 uppercase">
                    RDV réalisés
                  </th>
                  <th className="px-4 py-3 text-center text-xs font-medium text-slate-500 uppercase">
                    Offre
                  </th>
                  <th className="px-4 py-3 text-center text-xs font-medium text-slate-500 uppercase">
                    Deal
                  </th>
                  <th className="px-4 py-3 text-center text-xs font-medium text-slate-500 uppercase">
                    Refus
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-slate-200">
                {processStats.slice(0, 10).map((process) => (
                  <tr key={process.processId} className="hover:bg-slate-50">
                    <td className="px-4 py-3 text-sm text-slate-900 font-medium sticky left-0 bg-white">
                      {process.processName}
                    </td>
                    <td className="px-4 py-3 text-sm text-center font-bold text-slate-900">
                      {process.totalProspects}
                    </td>
                    {/* <td className="px-4 py-3 text-sm text-center text-blue-600">
                      {process.lead || 0}
                    </td> */}
                    <td className="px-4 py-3 text-sm text-center text-indigo-600">
                      {process.contacte || 0}
                    </td>
                    <td className="px-4 py-3 text-sm text-center text-cyan-600">
                      {process.aRepondu || 0}
                    </td>
                    <td className="px-4 py-3 text-sm text-center text-purple-600 font-medium">
                      {process.rdvRealise || 0}
                    </td>
                    <td className="px-4 py-3 text-sm text-center text-orange-600">
                      {process.offre || 0}
                    </td>
                    <td className="px-4 py-3 text-sm text-center text-green-600 font-medium">
                      {process.deal || 0}
                    </td>
                    <td className="px-4 py-3 text-sm text-center text-red-600 font-medium">
                      {process.refus || 0}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {processStats.length > 10 && (
              <p className="text-xs text-slate-500 mt-4 text-center">
                Affichage des 10 processus avec le plus de prospects
              </p>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default ProspectionStats;
